// "Verified this show" -- the launcher that replaces the old Google Sheets
// button, plus the in-page window it opens.
//
// The window is drawn through a portal onto document.body rather than where the
// button sits. #game carries a `transform: scale()` at most viewport widths and
// a transformed ancestor makes `position: fixed` resolve against that ancestor
// instead of the viewport, which would leave the window unable to reach the
// screen edges it is supposed to dock to.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useLiveShow, { formatConfirmedTime, shouldShowLauncher } from './liveShowStore';
import './LiveShow.css';

const STORAGE_KEY = 'wsbVerifiedWindow';

// Floating size limits. The upper bounds are also clamped to the viewport, so a
// window saved on a 4K monitor cannot arrive off-screen on a laptop.
const MIN_W = 260;
const MIN_H = 200;
const MAX_W = 720;
const MAX_H = 900;
const EDGE = 8;                 // keep this much of a gap at the viewport edge
const SNAP_PX = 56;             // drag this close to a side and it glues there
const DRAG_SLOP = 4;            // movement below this is a click, not a drag
const NARROW_QUERY = '(max-width: 700px)';

const DEFAULT_LAYOUT = { open: false, dock: 'none', x: null, y: null, w: 340, h: 430 };

function num(value, fallback) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

// localStorage is user-writable and survives across releases, so nothing that
// comes out of it is trusted: every field is re-validated on the way in.
function readLayout() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_LAYOUT };
    const saved = JSON.parse(raw) || {};
    return {
      open: saved.open === true,
      dock: saved.dock === 'left' || saved.dock === 'right' ? saved.dock : 'none',
      x: saved.x === null || saved.x === undefined ? null : num(saved.x, null),
      y: saved.y === null || saved.y === undefined ? null : num(saved.y, null),
      w: num(saved.w, DEFAULT_LAYOUT.w),
      h: num(saved.h, DEFAULT_LAYOUT.h),
    };
  } catch (e) {
    return { ...DEFAULT_LAYOUT };
  }
}

function writeLayout(layout) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(layout));
  } catch (e) {
    // Private mode or a full quota: the window still works, it just forgets.
  }
}

/** Force a layout back inside the current viewport. */
function clamp(layout) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const w = Math.max(MIN_W, Math.min(layout.w, Math.max(MIN_W, Math.min(MAX_W, vw - EDGE * 2))));
  const h = Math.max(MIN_H, Math.min(layout.h, Math.max(MIN_H, Math.min(MAX_H, vh - EDGE * 2))));

  // First run opens top-right, clear of the confirmation toasts bottom-right.
  const wantX = layout.x === null ? vw - w - 24 : layout.x;
  const wantY = layout.y === null ? 96 : layout.y;

  return {
    ...layout,
    w,
    h,
    x: Math.max(EDGE, Math.min(wantX, Math.max(EDGE, vw - w - EDGE))),
    y: Math.max(EDGE, Math.min(wantY, Math.max(EDGE, vh - h - EDGE))),
  };
}

function useNarrowViewport() {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia(NARROW_QUERY).matches
  );

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(NARROW_QUERY);
    const onChange = () => setNarrow(mq.matches);
    // addListener is the deprecated spelling, still the only one on older Safari.
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  return narrow;
}

const VerifiedShowWindow = () => {
  const live = useLiveShow();
  const narrow = useNarrowViewport();

  const [layout, setLayoutState] = useState(() => clamp(readLayout()));
  const layoutRef = useRef(layout);
  const setLayout = useCallback((updater) => {
    setLayoutState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      layoutRef.current = next;
      return next;
    });
  }, []);

  const panelRef = useRef(null);
  const dragRef = useRef(null);
  const resizeRef = useRef(null);
  const snapRef = useRef('none');
  const [snapPreview, setSnapPreview] = useState('none');
  const [dragging, setDragging] = useState(false);

  const showLauncher = shouldShowLauncher(live);
  const open = layout.open && showLauncher;

  const persist = useCallback(() => writeLayout(layoutRef.current), []);

  // Deliberate value (not updater) form: React batches updater functions until
  // render, so reading layoutRef straight after a functional setState would
  // save the previous layout. Committing the computed value keeps the ref, the
  // state and localStorage in step in one go.
  const commit = useCallback((next) => {
    layoutRef.current = next;
    setLayoutState(next);
    writeLayout(next);
  }, []);

  const setOpen = useCallback((open) => {
    commit({ ...layoutRef.current, open });
  }, [commit]);

  const setDock = useCallback((dock) => {
    commit(clamp({ ...layoutRef.current, dock }));
  }, [commit]);

  // Esc closes. The winner's dialog owns Esc while it is up -- closing both on
  // one keypress would be a surprise.
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      if (document.querySelector('.swal2-container')) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, setOpen]);

  // A rotated phone or a resized browser must not strand the window off-screen.
  useEffect(() => {
    let timer = null;
    const onResize = () => {
      setLayout((prev) => clamp(prev));
      clearTimeout(timer);
      timer = setTimeout(persist, 300);
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      clearTimeout(timer);
    };
  }, [setLayout, persist]);

  // Send focus to the panel when it opens so a keyboard user is standing on it
  // and Esc feels connected. Focus is deliberately NOT trapped: this is a
  // sidecar, not a modal, and Tab must be able to walk back out to the card.
  useEffect(() => {
    if (!open || !panelRef.current) return;
    try { panelRef.current.focus({ preventScroll: true }); } catch (e) { panelRef.current.focus(); }
  }, [open]);

  // ------------------------------------------------------------- dragging ---

  const onBarPointerDown = (event) => {
    if (narrow) return;                                   // bottom sheet: fixed
    if (event.button !== undefined && event.button !== 0) return;
    if (event.target.closest && event.target.closest('button')) return;
    const el = panelRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    dragRef.current = {
      pointerId: event.pointerId,
      grabX: event.clientX - rect.left,
      grabY: event.clientY - rect.top,
      startX: event.clientX,
      startY: event.clientY,
      moved: false,
    };
    try { event.currentTarget.setPointerCapture(event.pointerId); } catch (e) { /* ignore */ }
    event.preventDefault();
  };

  const onBarPointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    if (!drag.moved) {
      const travelled = Math.abs(event.clientX - drag.startX) + Math.abs(event.clientY - drag.startY);
      if (travelled < DRAG_SLOP) return;
      drag.moved = true;
      setDragging(true);
    }

    const width = layoutRef.current.w;
    const x = event.clientX - drag.grabX;
    const y = event.clientY - drag.grabY;

    // Dragging a docked window peels it off the edge; dock is decided again on
    // release from where it ended up.
    const next = x < SNAP_PX ? 'left' : (x + width > window.innerWidth - SNAP_PX ? 'right' : 'none');
    if (snapRef.current !== next) {
      snapRef.current = next;
      setSnapPreview(next);
    }

    setLayout((prev) => clamp({ ...prev, dock: 'none', x, y }));
  };

  const endDrag = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    try { event.currentTarget.releasePointerCapture(event.pointerId); } catch (e) { /* ignore */ }

    const snap = snapRef.current;
    snapRef.current = 'none';
    setSnapPreview('none');
    setDragging(false);
    if (!drag.moved) return;

    // Every pointermove already flushed into layoutRef, so this is the position
    // the window was actually released at.
    if (snap === 'none') persist();
    else commit(clamp({ ...layoutRef.current, dock: snap }));
  };

  // ------------------------------------------------------------ resizing ----

  const onGripPointerDown = (event) => {
    if (narrow) return;
    if (event.button !== undefined && event.button !== 0) return;
    resizeRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      w: layoutRef.current.w,
      h: layoutRef.current.h,
      dock: layoutRef.current.dock,
    };
    try { event.currentTarget.setPointerCapture(event.pointerId); } catch (e) { /* ignore */ }
    event.preventDefault();
    event.stopPropagation();
  };

  const onGripPointerMove = (event) => {
    const grip = resizeRef.current;
    if (!grip || grip.pointerId !== event.pointerId) return;
    const dx = event.clientX - grip.startX;
    const dy = event.clientY - grip.startY;

    if (grip.dock === 'left') setLayout((prev) => clamp({ ...prev, w: grip.w + dx }));
    else if (grip.dock === 'right') setLayout((prev) => clamp({ ...prev, w: grip.w - dx }));
    else setLayout((prev) => clamp({ ...prev, w: grip.w + dx, h: grip.h + dy }));
  };

  const endResize = (event) => {
    const grip = resizeRef.current;
    if (!grip || grip.pointerId !== event.pointerId) return;
    resizeRef.current = null;
    try { event.currentTarget.releasePointerCapture(event.pointerId); } catch (e) { /* ignore */ }
    persist();
  };

  // -------------------------------------------------------------- render ----

  if (!showLauncher) return null;   // not live, or nobody is hosting

  const count = live.tiles.length;
  const docked = layout.dock === 'left' || layout.dock === 'right';

  // Docked windows are laid out by CSS; only a floating one is positioned here.
  // The narrow-screen bottom sheet overrides all of it in LiveShow.css.
  const style = docked
    ? { width: `${layout.w}px` }
    : { left: `${layout.x}px`, top: `${layout.y}px`, width: `${layout.w}px`, height: `${layout.h}px` };

  const panel = (
    <div
      className={[
        'wsb-vw',
        docked ? `is-docked is-dock-${layout.dock}` : 'is-floating',
        dragging ? 'is-dragging' : '',
        snapPreview !== 'none' ? `will-dock-${snapPreview}` : '',
      ].filter(Boolean).join(' ')}
      style={style}
      ref={panelRef}
      role="dialog"
      aria-label="Verified this show"
      aria-modal="false"
      tabIndex={-1}
      data-html2canvas-ignore="true"
    >
      <div
        className="wsb-vw-bar"
        onPointerDown={onBarPointerDown}
        onPointerMove={onBarPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <span className="wsb-vw-dot" aria-hidden="true" />
        <span className="wsb-vw-title">Verified this show</span>
        <span className="wsb-vw-count">{count}</span>
        <span className="wsb-vw-tools">
          <button
            type="button"
            className={`wsb-vw-tool${layout.dock === 'left' ? ' is-on' : ''}`}
            onClick={() => setDock(layout.dock === 'left' ? 'none' : 'left')}
            title={layout.dock === 'left' ? 'Unglue from the left' : 'Glue to the left'}
            aria-label={layout.dock === 'left' ? 'Unglue from the left' : 'Glue to the left'}
          >
            <i className="fa-solid fa-angles-left" aria-hidden="true" />
          </button>
          <button
            type="button"
            className={`wsb-vw-tool${layout.dock === 'right' ? ' is-on' : ''}`}
            onClick={() => setDock(layout.dock === 'right' ? 'none' : 'right')}
            title={layout.dock === 'right' ? 'Unglue from the right' : 'Glue to the right'}
            aria-label={layout.dock === 'right' ? 'Unglue from the right' : 'Glue to the right'}
          >
            <i className="fa-solid fa-angles-right" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="wsb-vw-tool wsb-vw-close"
            onClick={() => setOpen(false)}
            title="Close"
            aria-label="Close verified this show"
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </span>
      </div>

      <div className="wsb-vw-body">
        {count === 0 ? (
          <p className="wsb-vw-empty">Nothing verified yet tonight.</p>
        ) : (
          <ul className="wsb-vw-list">
            {live.tiles.map((tile) => (
              <li className="wsb-vw-item" key={tile.key}>
                <span className="wsb-vw-item-main">
                  <span className="wsb-vw-item-text">{tile.tileText}</span>
                  {tile.category ? <span className="wsb-vw-item-cat">{tile.category}</span> : null}
                </span>
                <span className="wsb-vw-item-time" title={tile.confirmedAt || ''}>
                  {formatConfirmedTime(tile.confirmedAt)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="wsb-vw-foot">
        <span>Everything the hosts have confirmed so far tonight</span>
        <span className="wsb-vw-foot-tz">times in US Central</span>
      </div>

      <div
        className={`wsb-vw-grip${docked ? ' is-edge' : ''}`}
        onPointerDown={onGripPointerDown}
        onPointerMove={onGripPointerMove}
        onPointerUp={endResize}
        onPointerCancel={endResize}
        aria-hidden="true"
      />
    </div>
  );

  return (
    <>
      <button
        type="button"
        className="wsb-live-launcher"
        onClick={() => setOpen(!layout.open)}
        aria-expanded={open}
        aria-haspopup="dialog"
        title="Everything the hosts have verified so far tonight"
        data-html2canvas-ignore="true"
      >
        <span className="wsb-live-launcher-dot" aria-hidden="true" />
        <span className="wsb-live-launcher-text">Verified this show</span>
        <span className="wsb-live-launcher-count">{count}</span>
      </button>
      {open && typeof document !== 'undefined' ? createPortal(panel, document.body) : null}
    </>
  );
};

export default VerifiedShowWindow;
